import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  User, Flame, Target, Activity, Trophy, Calendar, ArrowUpRight, Link2,
} from "lucide-react";

/** Public profile: /u/:username. No auth required. */
export default function PublicProfile() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | private | notfound

  useEffect(() => {
    document.title = `@${username} · ExamBro`;
    api.get(`/profile/${username}`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch((e) => {
        if (e?.response?.status === 403) setState("private");
        else setState("notfound");
      });
  }, [username]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Profile link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center text-stone-500 font-mono text-sm">Loading profile…</div>;
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-stone-500">
        <div className="font-heading text-3xl font-bold text-stone-900 mb-2">404 · No such athlete</div>
        <div className="text-sm">No user with the handle @{username} on ExamBro.</div>
        <Link to="/" className="text-orange-600 mt-4 text-sm">← Back to ExamBro</Link>
      </div>
    );
  }
  if (state === "private") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-stone-500">
        <div className="font-heading text-3xl font-bold text-stone-900 mb-2">This profile is private</div>
        <div className="text-sm">@{username} has turned off public sharing.</div>
        <Link to="/" className="text-orange-600 mt-4 text-sm">← Back to ExamBro</Link>
      </div>
    );
  }

  const { user, stats, recent } = data;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Top nav */}
      <div className="border-b border-stone-200 bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading text-lg font-bold" data-testid="profile-back-to-home">
            <div className="h-6 w-6 rounded bg-orange-600 flex items-center justify-center text-white text-xs font-bold">E</div>
            <span>ExamBro</span>
          </Link>
          <button
            onClick={copyLink}
            className="text-sm text-stone-600 hover:text-orange-600 inline-flex items-center gap-1"
            data-testid="profile-copy-link"
          >
            <Link2 className="h-4 w-4" /> Share profile
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Hero */}
        <Card className="p-8 rounded-md border-stone-200 shadow-none">
          <div className="flex items-start gap-6">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-stone-900 text-4xl font-heading font-bold text-white flex-shrink-0">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-24 w-24 rounded-full object-cover" />
              ) : (
                (user.name?.[0] || "?").toUpperCase()
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-3xl font-bold" data-testid="profile-name">{user.name}</h1>
                <Badge variant="secondary" className="font-mono text-[10px] uppercase tracking-[0.2em]">@{user.username}</Badge>
                {stats.streak_days > 0 && (
                  <Badge className="bg-rose-600 hover:bg-rose-600 text-white font-mono text-[10px] uppercase tracking-[0.2em]" data-testid="profile-streak-badge">
                    <Flame className="h-3 w-3 mr-1" /> {stats.streak_days}-day streak
                  </Badge>
                )}
              </div>
              {user.member_since && (
                <div className="text-xs text-stone-500 mt-2 font-mono flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Member since {new Date(user.member_since).toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Stats grid */}
        {stats.total_attempts > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="profile-stats">
            <StatBox icon={Activity} label="Mocks taken" value={stats.total_attempts} accent="#ea580c" />
            <StatBox icon={Target} label="Avg accuracy" value={`${stats.avg_accuracy}%`} accent="#059669" />
            <StatBox icon={Trophy} label="Best score" value={`${stats.best_score_pct}%`} accent="#0d9488" />
            <StatBox icon={Flame} label="Study days" value={stats.unique_days} accent="#e11d48" />
            <StatBox icon={User} label="Points" value={stats.total_points} accent="#7c3aed" />
          </div>
        ) : (
          <Card className="p-8 text-center text-stone-500">
            @{user.username} hasn't taken any mocks yet.
          </Card>
        )}

        {/* Best section */}
        {stats.best_section && (
          <Card className="p-6 rounded-md border-stone-200 shadow-none">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Trophy className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Sharpest section</div>
                <div className="font-heading text-lg font-semibold">{stats.best_section}</div>
              </div>
            </div>
          </Card>
        )}

        {/* Recent attempts */}
        {recent && recent.length > 0 && (
          <Card className="p-6 rounded-md border-stone-200 shadow-none">
            <h3 className="font-heading text-lg font-semibold mb-4">Recent activity</h3>
            <div className="space-y-2" data-testid="profile-recent">
              {recent.map((r) => (
                <div key={r.attempt_id} className="flex items-center gap-3 p-3 border border-stone-200 rounded-md">
                  <div className="h-8 w-8 rounded-md bg-stone-900 flex items-center justify-center flex-shrink-0">
                    <Activity className="h-4 w-4 text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.exam_title}</div>
                    <div className="font-mono text-[10px] text-stone-500">
                      {r.date ? new Date(r.date).toLocaleDateString() : ""}
                    </div>
                  </div>
                  <div className="font-heading text-xl font-bold tabular-nums text-orange-600">
                    {r.score_pct}%
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* CTA */}
        <Card className="p-6 rounded-md border-stone-200 shadow-none bg-gradient-to-br from-orange-50 to-white">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-heading text-lg font-bold">Want a profile like this?</div>
              <div className="text-sm text-stone-600 mt-1">
                Upload any past paper, get instant AI grading, track improvement over time.
              </div>
            </div>
            <Link to="/login">
              <button className="rounded-md bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium px-4 py-2 inline-flex items-center gap-1">
                Start free <ArrowUpRight className="h-4 w-4" />
              </button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-4 rounded-md border-stone-200 shadow-none">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</span>
        <Icon className="h-4 w-4" style={{ color: accent }} />
      </div>
      <div className="font-heading text-2xl font-bold tabular-nums" style={{ color: accent }}>
        {value}
      </div>
    </Card>
  );
}
